import { useState, useEffect, useCallback } from 'react';
import { deepMerge } from '@agilepoint/utils-common';
import { DeepPartial } from '@agilepoint/utils-type';
import { ThemeCompiler, IThemeSemantics, ITheme } from '../types';
import { getBaseSemantics } from './base-theme.semantics';
import { getBaseCompilers } from './base-theme-compilers';

type ChangeListener = () => void;

class ThemeRegistry<TS extends IThemeSemantics = IThemeSemantics, TTheme extends ITheme = ITheme, TC extends ThemeCompiler<TS, TTheme> = ThemeCompiler<TS, TTheme>> {
    private _baseCompilers: TC[] = [];
    private _subscribers: ChangeListener[] = [];
    private _themes = new Map<string, Readonly<TTheme>>();
    private _themeCompilerVariables = new Map<string, [DeepPartial<TS>, ...TC[]]>();

    addCompilers(...compilers: TC[]) {
        this._baseCompilers.push(...compilers);
        this.onCompilersUpdate();
    }

    removeCompiler(compiler: TC) {
        this._baseCompilers = this._baseCompilers.filter(c => c !== compiler);
        this.onCompilersUpdate();
    }

    getTheme(themeName: string): Readonly<TTheme> {
        const theme = this._themes.get(themeName);
        if (theme === undefined) {
            throw new Error('Theme not found');
        }
        return theme;
    }

    registerTheme = (name: string, theme: TTheme) => {
        this._themes.set(name, Object.freeze(theme));
    }

    registerThemeUsingCompilers = (name: string, semantics: DeepPartial<TS> = {}, ...themeCompilers: TC[]) => {
        this._themeCompilerVariables.set(name, [semantics, ...themeCompilers]);
        const theme = this.compileTheme(semantics, themeCompilers);
        this.registerTheme(name, theme);
    }

    subscribe(listener: ChangeListener) {
        this._subscribers.push(listener);
    }

    unsubscribe(listener: ChangeListener) {
        this._subscribers = this._subscribers.filter(l => l !== listener);
    }

    private compileTheme = (semantics: DeepPartial<TS>, themeCompilers: TC[]): TTheme => {
        const mergedSemantics = deepMerge({} as any, getBaseSemantics(), semantics) as TS;
        const compilers = [...this._baseCompilers, ...themeCompilers];
        return compilers.reduce((theme, compilier) => deepMerge({} as any, theme, compilier(mergedSemantics as TS)), {}) as TTheme;
    }

    private recompileTheme = (themeName: string) => {
        const compilerArguments = this._themeCompilerVariables.get(themeName);
        if (compilerArguments === undefined) {
            return;
        }
        const [semantics, ...themeCompilers] = compilerArguments;
        this._themes.set(themeName, this.compileTheme(semantics, themeCompilers));
    }

    private onCompilersUpdate = () => {
        Array.from(this._themeCompilerVariables.keys()).map(this.recompileTheme);
        this._subscribers.forEach(s => s());
    }
}

export const themeRegistry = new ThemeRegistry();

themeRegistry.addCompilers(...getBaseCompilers());

export function useRegisteredTheme(themeName: string) {
    const [theme, setTheme] = useState(themeRegistry.getTheme(themeName));

    const _updateTheme = useCallback(() => {
        setTheme(themeRegistry.getTheme(themeName));
    }, [themeName]);

    useEffect(() => {
        themeRegistry.subscribe(_updateTheme);
        return () => themeRegistry.unsubscribe(_updateTheme);
    }, [_updateTheme]);

    return theme;
}
